import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { deleteVehicle, VEHICLES_QUERY_KEY } from './VehicleManager.service'
import type { VehicleData } from './VehicleManager.types'

interface DeleteVehicleDialogPayload {
  vehicle: VehicleData
}

interface DeleteVehicleDialogProps {
  payload: DeleteVehicleDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const DeleteVehicleDialog = ({ payload, open, onAction, onClose }: DeleteVehicleDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, formState, setError } = useForm()
  const queryClient = useQueryClient()
  const deleteVehicleMutation = useMutation({
    mutationFn: deleteVehicle,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [VEHICLES_QUERY_KEY] }),
  })
  const [deletionConfirmed, setDeletionConfirmed] = useState(false)

  useEffect(() => {
    setDeletionConfirmed(false)
  }, [payload])

  const onSubmit: SubmitHandler<object> = async (): Promise<void> => {
    await handleFormSubmission(() => deleteVehicleMutation.mutateAsync({
      licensePlate: payload.vehicle.licensePlate,
      vehicleType: payload.vehicle.vehicleType,
    }), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction()
        analyticsEvent('polestar_cs_vehicle_deleted')
      },
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={`${payload.vehicle.licensePlate} — ${t('screens.cs.vehicle_manager.card.actions.delete')}`}
      actions={
        !payload.vehicle.telematicsReady && (
          <Button
            type="submit"
            color="error"
            variant="contained"
            loading={formState.isSubmitting}
            disabled={!deletionConfirmed}
            onClick={handleSubmit(onSubmit)}
          >
            {t('screens.cs.vehicle_manager.delete_vehicle.delete')}
          </Button>
        )
      }
      onSubmit={handleSubmit(onSubmit)}
      maxWidth="xs"
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      {payload.vehicle.telematicsReady ? (
        <Typography>
          {t('screens.cs.vehicle_manager.delete_vehicle.telematics_notice')}
        </Typography>
      ) : (
        <>
          <Typography>
            {t('screens.cs.vehicle_manager.delete_vehicle.confirmation')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                value={deletionConfirmed}
                checked={deletionConfirmed}
                onChange={(): void => setDeletionConfirmed(!deletionConfirmed)}
              />
            }
            label={t('shared.manager.deletion_confirm_checkbox')}
          />
        </>
      )}
    </KNDialog>
  )
}

export default DeleteVehicleDialog
